import { useCallback } from "react";
import useCommandFlowStore from "../../store/commandFlowStore";
import { getTranslation } from "../../config/translations";

export const useInputHandling = (
  currentStepChoices,
  commands,
  currentPrompt
) => {
  const {
    input,
    setInput,
    history,
    historyIndex,
    setHistoryIndex,
    setSuggestionText,
  } = useCommandFlowStore();

  const getVisibleCommands = useCallback(
    () =>
      Object.entries(commands)
        .filter(([_, cmd]) => !cmd.hidden)
        .map(([name]) => name),
    [commands]
  );

  const handleTabCompletion = useCallback(() => {
    const translations = useCommandFlowStore.getState().translations;

    // In-session: autocomplete/suggest from choices
    if (currentPrompt && currentStepChoices && currentStepChoices.length > 0) {
      const matches = currentStepChoices.filter((choice) =>
        choice.startsWith(input.trim())
      );

      if (matches.length === 1) {
        setInput(matches[0]);
        setSuggestionText("");
      } else if (matches.length > 1) {
        setSuggestionText(
          `${getTranslation("suggestions", translations)}${matches.join(", ")}`
        );
      } else {
        setSuggestionText(
          getTranslation("noSuggestions", translations) || "No suggestions."
        );
      }
      return;
    } // At command prompt: suggest commands
    if (!currentPrompt) {
      const translations = useCommandFlowStore.getState().translations;
      const visibleCommands = getVisibleCommands();
      const matches = visibleCommands.filter((cmd) =>
        cmd.startsWith(input.trim())
      );

      if (matches.length === 1) {
        setInput(matches[0]);
        setSuggestionText("");
      } else if (matches.length > 1) {
        setSuggestionText(
          `${getTranslation("suggestions", translations)}${matches.join(", ")}`
        );
      }
    }
  }, [
    currentPrompt,
    currentStepChoices,
    input,
    setInput,
    setSuggestionText,
    getVisibleCommands,
  ]);

  const navigateHistory = useCallback(
    (direction) => {
      if (history.length === 0) return;

      const reversedHistory = [...history].reverse();
      const newIndex =
        direction === "up"
          ? historyIndex === null
            ? reversedHistory.length - 1
            : Math.max(0, historyIndex - 1)
          : historyIndex === null
          ? null
          : Math.min(historyIndex + 1, reversedHistory.length - 1);

      setInput(newIndex === null ? "" : reversedHistory[newIndex]);
      setHistoryIndex(newIndex);
    },
    [history, historyIndex, setInput, setHistoryIndex]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Tab") {
        e.preventDefault();
        handleTabCompletion();
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown") {
        e.preventDefault();
        navigateHistory(e.key === "ArrowUp" ? "up" : "down");
      }
    },
    [handleTabCompletion, navigateHistory]
  );

  return {
    handleKeyDown,
    getVisibleCommands,
  };
};
